import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Super-admin: "what can still charge this customer in Stripe?" + hard stop. Opens as a modal per salon.
export default function AdminBillingAudit({ salon, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await axios.get(`${API}/admin/billing/audit/${salon.id}`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ошибка аудита"); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const stop = async () => {
    if (!window.confirm(`Остановить ВСЕ списания для «${salon.salon_name}»? Все подписки Stripe будут отменены, карты отвязаны, доступ к ИИ закрыт.`)) return;
    setBusy(true);
    try { const r = await axios.post(`${API}/admin/billing/stop/${salon.id}`); toast.success(`Готово: отменено подписок ${r.data.subscriptions_cancelled}`); await load(); }
    catch (e) { toast.error("Не удалось"); } finally { setBusy(false); }
  };

  const fmt = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString() : "—");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-billing-audit">
      <div className="bg-white text-black rounded-xl border-2 border-black max-w-3xl w-full max-h-[90vh] overflow-auto p-5" style={{ color: "#111" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">💳 Аудит биллинга — {salon.salon_name}</h3>
            <p className="text-xs text-gray-500">{salon.user_email} · {salon.is_paid ? "PAID" : "не оплачен"} · {salon.plan}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="admin-billing-close">✕</Button>
        </div>
        {!data && <p className="mt-4 text-sm text-gray-500">{busy ? "Загрузка из Stripe…" : "—"}</p>}
        {data && (
          <>
            <div className={`mt-4 p-3 rounded-lg border-2 ${data.risk ? "bg-red-50 border-red-400" : "bg-green-50 border-green-400"}`} data-testid="admin-billing-risk">
              {data.risk ? "⚠️ В Stripe ещё есть активные подписки или сохранённые карты — возможны списания." : "✅ Активных подписок и карт в Stripe нет — списания невозможны."}
              {data.salon.billing_fully_stopped_at && <div className="text-xs mt-1">Полная остановка выполнена: {new Date(data.salon.billing_fully_stopped_at).toLocaleString()}</div>}
            </div>
            {data.customers.map((c) => (
              <div key={c.id} className="mt-4 border rounded-lg p-3 text-sm">
                <div className="font-mono font-semibold">{c.id} {c.error && <span className="text-red-600">— {c.error}</span>}</div>
                <div className="mt-2"><b>Подписки:</b> {c.subscriptions?.length ? c.subscriptions.map((s) => (
                  <div key={s.id} className="font-mono text-xs">{s.id} · <span className={s.status === "canceled" ? "text-gray-400" : "text-red-600 font-bold"}>{s.status}</span> · €{s.amount} · до {fmt(s.current_period_end)}</div>
                )) : "нет"}</div>
                <div className="mt-2"><b>Открытые счета:</b> {c.open_invoices?.length ? c.open_invoices.map((i) => <div key={i.id} className="font-mono text-xs">{i.id} · {i.status} · €{i.amount_due} · попыток {i.attempt_count}</div>) : "нет"}</div>
                <div className="mt-2"><b>Карты:</b> {c.cards?.length ? c.cards.map((k) => <span key={k.id} className="font-mono text-xs mr-2">•••• {k.last4} ({k.exp})</span>) : "нет"}</div>
                <div className="mt-2"><b>Последние платежи:</b> {c.recent_payments?.length ? c.recent_payments.slice(0, 5).map((p) => (
                  <div key={p.id} className="font-mono text-xs">{fmt(p.created)} · €{p.amount} · {p.status} · {p.description || ""}</div>
                )) : "нет"}</div>
              </div>
            ))}
            <div className="mt-4 flex gap-2">
              <Button data-testid="admin-billing-refresh" variant="outline" onClick={load} disabled={busy}>Обновить</Button>
              <Button data-testid="admin-billing-stop" onClick={stop} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">🛑 Остановить все списания</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
